<template>
  <div class="employees-page">
    <DashboardPageHeader
      title="Team"
      subtitle="Manage who can access your business. Different roles have different permissions."
    >
      <template v-if="!loading && canEdit" #actions>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-account-plus"
          :disabled="loading"
          @click="onAddEmployeeClick"
        >
          Add team member
        </v-btn>
      </template>
    </DashboardPageHeader>

    <div v-if="loading" class="employees-loading">
      <BrandedLoader text="Loading team…" spinner-color="primary" />
    </div>

    <div v-else-if="loadError" class="employees-empty" role="alert">
      <v-icon class="employees-empty-icon">mdi-alert-circle-outline</v-icon>
      <h2>We couldn't load your team</h2>
      <p>{{ loadError }}</p>
      <v-btn
        class="mt-3"
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        @click="loadData"
      >
        Try again
      </v-btn>
    </div>

    <template v-else>
      <div v-if="!canEdit" class="employees-readonly-banner" role="status">
        <v-icon class="employees-readonly-icon">mdi-lock-outline</v-icon>
        <div>
          <strong>You're viewing in read-only mode.</strong>
          Only the owner or an administrator can add, edit, or remove team members.
        </div>
      </div>

      <div v-if="actionError" class="employees-status-row" role="alert">
        <div class="employees-status employees-status--error">
          <v-icon class="employees-status-icon">mdi-alert-circle</v-icon>
          {{ actionError }}
        </div>
      </div>
      <div v-else-if="actionSuccess" class="employees-status-row" role="status">
        <div class="employees-status employees-status--success">
          <v-icon class="employees-status-icon">mdi-check-circle</v-icon>
          {{ actionSuccess }}
        </div>
      </div>

      <div class="employees-sections-grid">
        <EmployeeSection
          title="Owner"
          empty-text="No Owner found for this business."
          :rows="owners"
          :can-edit="false"
          :can-delete-row="() => false"
          locked-hint="Set when this business was created"
          permissions="Full access to every feature, including remote stamping, subscription and billing, branding and reward card settings, and team management. The Owner is also the only person who can delete the business."
        />

        <EmployeeSection
          title="Administrators"
          empty-text="No Administrators yet."
          :rows="administrators"
          :can-edit="canEdit"
          :can-delete-row="(row) => canModifyRow(row)"
          permissions="Full access to day-to-day features, including remote stamping, branding and reward card settings, and adding, editing or removing team members. Cannot manage the subscription or billing, and cannot change or remove the Owner."
          @edit="onEditEmployee"
          @delete="onDeleteEmployeeRequest"
        />

        <EmployeeSection
          title="Employees"
          empty-text="No Employees yet. Add one to get started."
          :rows="employees"
          :can-edit="canEdit"
          :can-delete-row="(row) => canModifyRow(row)"
          permissions="Can scan and stamp cards in person, view customers and see analytics. Cannot stamp remotely, manage the subscription, change branding or reward card settings, or see other team members."
          @edit="onEditEmployee"
          @delete="onDeleteEmployeeRequest"
        />
      </div>
    </template>

    <EmployeeForm
      v-model="formDialogOpen"
      :mode="formMode"
      :initial-value="formInitialValue"
      :submitting="formSubmitting"
      :error-message="formError"
      :lock-role="formLockRole"
      :return-focus-to="formReturnFocusTo"
      @submit="onFormSubmit"
      @cancel="onFormCancel"
    />

    <!-- Destructive confirm -->
    <v-dialog
      v-model="confirmDeleteOpen"
      :max-width="smAndDown ? undefined : 460"
      :fullscreen="smAndDown"
      persistent
    >
      <v-card class="confirm-card">
        <v-card-title class="confirm-title">
          Are you sure you want to remove
          {{ pendingDelete?.role === "Administrator" ? "this Administrator" : "this team member" }}?
        </v-card-title>
        <v-card-text class="confirm-text">
          <p>
            This will revoke access for
            <strong>{{ pendingDelete?.name || pendingDelete?.email }}</strong>
            ({{ pendingDelete?.email }}). They won't be able to sign in or
            scan your customers' rewards cards anymore.
          </p>
          <p v-if="deleteError" class="confirm-error" role="alert">
            {{ deleteError }}
          </p>
        </v-card-text>
        <v-card-actions class="confirm-actions">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="deleteSubmitting"
            @click="onCancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleteSubmitting"
            :disabled="deleteSubmitting"
            @click="onConfirmDelete"
          >
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Account created success modal -->
    <v-dialog
      v-model="successOpen"
      :max-width="smAndDown ? undefined : 440"
      :fullscreen="smAndDown"
      persistent
    >
      <v-card class="success-card">
        <v-card-text class="success-text">
          <div class="success-icon-wrap" aria-hidden="true">
            <v-icon class="success-icon">mdi-check-circle</v-icon>
          </div>
          <h2 class="success-title">Account created</h2>
          <p class="success-body">
            <template v-if="successInfo?.emailSent === false">
              We added <strong>{{ successInfo?.email }}</strong> to your team,
              but we couldn't email them their sign-in details. Please contact
              support so we can resend their welcome email.
            </template>
            <template v-else>
              We've added <strong>{{ successInfo?.email }}</strong> to your team
              and emailed them their sign-in instructions.
            </template>
          </p>
        </v-card-text>
        <v-card-actions class="success-actions">
          <v-spacer />
          <v-btn color="primary" variant="flat" @click="onDismissSuccess">
            Done
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import DashboardPageHeader from "@/components/Molecules/DashboardPageHeader.vue";
import BrandedLoader from "@/components/Molecules/BrandedLoader.vue";
import EmployeeSection from "@/components/Molecules/EmployeeSection.vue";
import EmployeeForm from "@/components/Organisms/EmployeeForm.vue";
import {
  getEmployeesData,
  getCurrentUserRole,
  getBusinessDisplayName,
} from "@/firebase-business.js";
import {
  addEmployee,
  updateEmployee,
  deleteEmployee,
} from "@/employeeHandler.js";
import { authStore, refreshEmployers } from "@/stores/authStore.js";
import { track } from "@/analytics.js";

export default {
  name: "DashboardEmployeesComponent",
  components: { DashboardPageHeader, BrandedLoader, EmployeeForm, EmployeeSection },
  setup() {
    const display = useDisplay();
    const smAndDown = computed(() => display.smAndDown.value);
    const selectedBusinessId = inject("selectedBusinessId");
    const loading = ref(true);
    const loadError = ref("");
    const role = ref("Employee");
    const rows = ref([]);
    const businessDisplayName = ref("");
    const actionError = ref("");
    const actionSuccess = ref("");

    const formDialogOpen = ref(false);
    const formMode = ref("add"); // 'add' | 'edit'
    const formInitialValue = ref({ name: "", email: "", role: "Employee" });
    const formSubmitting = ref(false);
    const formError = ref("");
    // When editing self, the role must be locked - defence-in-depth against
    // self-demotion (the handler also throws if anyone bypasses this).
    const formLockRole = ref(false);
    // The trigger element that opened the dialog. We restore focus to it
    // after the dialog closes so keyboard nav lands somewhere sensible.
    const formReturnFocusTo = ref(null);
    // Hold a reference to the row being edited so we can pass the *original*
    // email through to updateEmployee - the form may have a new one.
    let editingOriginal = null;

    const confirmDeleteOpen = ref(false);
    const pendingDelete = ref(null);
    const deleteSubmitting = ref(false);
    const deleteError = ref("");

    const successOpen = ref(false);
    const successInfo = ref(null);

    const canEdit = computed(
      () => role.value === "Owner" || role.value === "Administrator"
    );

    const owners = computed(() =>
      rows.value.filter((r) => r.role === "Owner")
    );
    const administrators = computed(() =>
      rows.value.filter((r) => r.role === "Administrator")
    );
    const employees = computed(() =>
      rows.value.filter((r) => r.role !== "Owner" && r.role !== "Administrator")
    );

    const canModifyRow = (row) => {
      if (!row) return false;
      if (row.role === "Owner") return false;
      const me = (authStore.user?.email || "").toLowerCase();
      if (me && row.email && row.email.toLowerCase() === me) return false;
      return canEdit.value;
    };

    const flashSuccess = (message) => {
      actionSuccess.value = message;
      actionError.value = "";
      setTimeout(() => {
        if (actionSuccess.value === message) actionSuccess.value = "";
      }, 4000);
    };

    const loadData = async () => {
      const id = selectedBusinessId?.value;
      if (!id) {
        loading.value = false;
        return;
      }
      loading.value = true;
      loadError.value = "";
      try {
        const [list, resolvedRole, displayName] = await Promise.all([
          getEmployeesData(id),
          getCurrentUserRole(id, authStore.user?.email || ""),
          getBusinessDisplayName(id).catch(() => ""),
        ]);
        rows.value = list;
        role.value = resolvedRole || "Employee";
        businessDisplayName.value = displayName || "";
        track("dashboard_employees_viewed", { business_id: id });
      } catch (e) {
        loadError.value =
          "Something went wrong loading your team. Please refresh and try again.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadData);
    watch(() => selectedBusinessId?.value, loadData);

    const onAddEmployeeClick = (event) => {
      if (!canEdit.value) {
        actionError.value =
          "Only the owner or an administrator can add team members.";
        return;
      }
      formMode.value = "add";
      formInitialValue.value = { name: "", email: "", role: "Employee" };
      formError.value = "";
      formLockRole.value = false;
      formReturnFocusTo.value =
        (event && event.currentTarget) || document.activeElement || null;
      editingOriginal = null;
      formDialogOpen.value = true;
    };

    const onEditEmployee = (row, triggerEl) => {
      if (!canEdit.value || row.role === "Owner") return;
      formMode.value = "edit";
      formInitialValue.value = { ...row };
      formError.value = "";
      // Self-edit: allow renaming but lock the role. Users still get a
      // useful affordance without the foot-gun of demoting themselves.
      const me = (authStore.user?.email || "").toLowerCase();
      const isSelf =
        !!me && !!row.email && row.email.toLowerCase() === me;
      formLockRole.value = isSelf;
      formReturnFocusTo.value = triggerEl || document.activeElement || null;
      editingOriginal = { ...row };
      formDialogOpen.value = true;
    };

    const onFormCancel = () => {
      formError.value = "";
      editingOriginal = null;
    };

    const onFormSubmit = async (payload) => {
      if (!canEdit.value) {
        formError.value =
          "You don't have permission to make that change.";
        return;
      }
      const id = selectedBusinessId?.value;
      if (!id) return;

      formSubmitting.value = true;
      formError.value = "";
      try {
        if (formMode.value === "add") {
          const result = await addEmployee(id, payload, {
            businessDisplayName: businessDisplayName.value,
          });
          successInfo.value = {
            email: payload.email,
            emailSent: !!result.emailSent,
          };
          successOpen.value = true;
          formDialogOpen.value = false;
        } else {
          await updateEmployee(id, editingOriginal, payload, {
            currentUserEmail: authStore.user?.email || "",
          });
          formDialogOpen.value = false;
          flashSuccess(`Updated ${payload.email}.`);
        }
        // Always re-fetch - the round trip preserves any fields we don't
        // know about (iOS may store extras on the employee record).
        await loadData();
        // If the signed-in user updated their own record (e.g. renamed
        // themselves), refresh the employer list so the dashboard stays
        // consistent.
        await refreshEmployers();
      } catch (e) {
        formError.value =
          (e && e.message) ||
          "We couldn't save that. Please try again in a moment.";
      } finally {
        formSubmitting.value = false;
      }
    };

    const onDeleteEmployeeRequest = (row) => {
      if (!canModifyRow(row)) return;
      pendingDelete.value = { ...row };
      deleteError.value = "";
      confirmDeleteOpen.value = true;
    };

    const onCancelDelete = () => {
      if (deleteSubmitting.value) return;
      confirmDeleteOpen.value = false;
      pendingDelete.value = null;
      deleteError.value = "";
    };

    const onConfirmDelete = async () => {
      if (!canModifyRow(pendingDelete.value)) {
        deleteError.value = "You can't remove that team member.";
        return;
      }
      const id = selectedBusinessId?.value;
      if (!id || !pendingDelete.value) return;
      deleteSubmitting.value = true;
      deleteError.value = "";
      try {
        await deleteEmployee(id, pendingDelete.value);
        confirmDeleteOpen.value = false;
        flashSuccess(`${pendingDelete.value.email} removed from your team.`);
        pendingDelete.value = null;
        await loadData();
      } catch (e) {
        deleteError.value =
          (e && e.message) ||
          "We couldn't remove that team member. Please try again.";
      } finally {
        deleteSubmitting.value = false;
      }
    };

    const onDismissSuccess = () => {
      successOpen.value = false;
      successInfo.value = null;
    };

    return {
      smAndDown,
      loading,
      loadError,
      role,
      canEdit,
      owners,
      administrators,
      employees,
      actionError,
      actionSuccess,
      formDialogOpen,
      formMode,
      formInitialValue,
      formSubmitting,
      formError,
      formLockRole,
      formReturnFocusTo,
      confirmDeleteOpen,
      pendingDelete,
      deleteSubmitting,
      deleteError,
      successOpen,
      successInfo,
      canModifyRow,
      loadData,
      onAddEmployeeClick,
      onEditEmployee,
      onFormSubmit,
      onFormCancel,
      onDeleteEmployeeRequest,
      onCancelDelete,
      onConfirmDelete,
      onDismissSuccess,
    };
  },
};
</script>

<style scoped>
.employees-page :deep(.v-btn),
.confirm-card :deep(.v-btn),
.success-card :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 999px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Primary green pill - matches the header "Get started" button. */
.employees-page :deep(.v-btn--variant-flat.text-primary),
.employees-page :deep(.v-btn.bg-primary),
.confirm-card :deep(.v-btn--variant-flat.text-primary),
.confirm-card :deep(.v-btn.bg-primary),
.success-card :deep(.v-btn--variant-flat.text-primary),
.success-card :deep(.v-btn.bg-primary) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.employees-page :deep(.v-btn--variant-flat.text-primary:hover),
.employees-page :deep(.v-btn.bg-primary:hover),
.confirm-card :deep(.v-btn--variant-flat.text-primary:hover),
.confirm-card :deep(.v-btn.bg-primary:hover),
.success-card :deep(.v-btn--variant-flat.text-primary:hover),
.success-card :deep(.v-btn.bg-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Secondary outlined buttons - preserve hierarchy, same hover pop. */
.employees-page :deep(.v-btn--variant-outlined.text-primary),
.success-card :deep(.v-btn--variant-outlined.text-primary) {
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.employees-page :deep(.v-btn--variant-outlined.text-primary:hover),
.success-card :deep(.v-btn--variant-outlined.text-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
}

/* Destructive (Remove) - red-tinted pop. */
.confirm-card :deep(.v-btn--variant-flat.text-error),
.confirm-card :deep(.v-btn.bg-error) {
  box-shadow: 0 2px 8px rgba(176, 0, 32, 0.2) !important;
}

.confirm-card :deep(.v-btn--variant-flat.text-error:hover),
.confirm-card :deep(.v-btn.bg-error:hover) {
  box-shadow: 0 4px 16px rgba(176, 0, 32, 0.35) !important;
  transform: translateY(-1px);
}

/* Ghost / text buttons (Cancel) - subtle pop on hover. */
.employees-page :deep(.v-btn--variant-text:hover),
.confirm-card :deep(.v-btn--variant-text:hover),
.success-card :deep(.v-btn--variant-text:hover) {
  transform: translateY(-1px);
}

/* Disabled state must not lift. */
.employees-page :deep(.v-btn--disabled:hover),
.confirm-card :deep(.v-btn--disabled:hover),
.success-card :deep(.v-btn--disabled:hover) {
  transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.employees-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.employees-sections-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

@media (max-width: 960px) {
  .employees-sections-grid {
    grid-template-columns: 1fr;
  }
}

.employees-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.employees-empty {
  text-align: center;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 24px;
}

.employees-empty-icon {
  color: var(--lulu-text-subtle);
  font-size: 48px !important;
  margin-bottom: 12px;
}

.employees-readonly-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--lulu-surface);
  border: 1px solid var(--lulu-border);
  border-radius: var(--lulu-radius-card);
  padding: 12px 16px;
  color: var(--lulu-text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.employees-readonly-icon {
  color: var(--lulu-text-muted) !important;
}

.employees-status-row {
  min-height: 24px;
}

.employees-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.employees-status--success {
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
}

.employees-status--error {
  background: var(--lulu-danger-soft);
  color: var(--lulu-danger);
}

.employees-status-icon {
  font-size: 16px !important;
}

/* Row-level styles (section card, list, row, pills, action buttons) now
   live with the EmployeeSection SFC so they travel with the component. */

.confirm-card {
  border-radius: var(--lulu-radius-card);
}

.confirm-title {
  font-size: 18px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  padding: 20px 20px 4px;
}

.confirm-text {
  font-size: 14px;
  color: var(--lulu-text-strong);
  line-height: 1.5;
  padding: 8px 20px 0 !important;
}

.confirm-error {
  margin: 12px 0 0;
  color: var(--lulu-danger);
  font-size: 13px;
  font-weight: 500;
}

.confirm-actions {
  padding: 12px 20px 16px !important;
}

.success-card {
  border-radius: var(--lulu-radius-card);
}

.success-text {
  padding: 32px 28px 8px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.success-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--lulu-primary-soft, rgba(0, 109, 62, 0.12));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.success-icon {
  color: rgb(0, 109, 62) !important;
  font-size: 44px !important;
}

.success-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
  color: var(--lulu-text-strong);
}

.success-body {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--lulu-text-muted);
  max-width: 360px;
}

.success-actions {
  padding: 8px 20px 20px !important;
}
</style>
